import { useEffect, useState } from "react";
import { Flower2, Sunrise } from "lucide-react";
import { scrollToId } from "./Reveal";
import { useLang } from "@/i18n/LangContext";

const LINKS = [
  ["nav.about", "about", "nav-link-about"],
  ["nav.meditations", "meditations", "nav-link-meditations"],
  ["events.navLabel", "events", "nav-link-events"],
  ["nav.journey", "yatra", "nav-link-journey"],
  ["nav.gallery", "gallery", "nav-link-gallery"],
  ["nav.testimonials", "testimonials", "nav-link-testimonials"],
  ["nav.contact", "contact", "nav-link-contact"],
];

const THEME_META = {
  "baby-pink": { label: "Dawn Petal", Icon: Flower2 },
  "early-sky": { label: "Morning Sky", Icon: Sunrise },
};

const LANGS = [
  ["en", "EN"],
  ["hi", "हिं"],
  ["gu", "ગુ"],
];

export default function Navbar({ theme, onToggleTheme }) {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const meta = THEME_META[theme] || THEME_META["baby-pink"];

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 border-b"
      style={{
        backgroundColor: scrolled ? "var(--bg-glass)" : "transparent",
        borderColor: scrolled ? "var(--border-delicate)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
      }}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-baseline gap-2 text-left"
          data-testid="nav-brand"
        >
          <span className="font-cinzel text-lg tracking-[0.2em]" style={{ color: "var(--text-primary)" }}>
            MAA PREM MAHIMA
          </span>
          <span className="hidden font-mono-alt text-[10px] uppercase tracking-[0.3em] sm:inline" style={{ color: "var(--text-muted)" }}>
            Osho Universal
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map(([key, id, testid]) => (
            <button
              key={testid}
              data-testid={testid}
              onClick={() => scrollToId(id)}
              className="text-sm tracking-wide transition-colors"
              style={{ color: "var(--text-secondary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-gold)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
            >
              {t(key)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-switcher"
            className="flex items-center rounded-full border p-1"
            style={{ borderColor: "var(--border-delicate)" }}
          >
            {LANGS.map(([code, label]) => (
              <button
                key={code}
                data-testid={`lang-switch-${code}`}
                onClick={() => setLang(code)}
                className="rounded-full px-2.5 py-1 text-xs font-medium transition-colors"
                style={
                  lang === code
                    ? { backgroundColor: "var(--pill-tag-bg)", color: "var(--pill-tag-text)" }
                    : { color: "var(--text-muted)" }
                }
              >
                {label}
              </button>
            ))}
          </div>
          <button
            data-testid="theme-toggle-button"
            onClick={onToggleTheme}
            aria-label="Switch color theme"
            className="flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium tracking-wide"
            style={{
              borderColor: "var(--border-delicate)",
              backgroundColor: "var(--pill-tag-bg)",
              color: "var(--pill-tag-text)",
            }}
          >
            <meta.Icon size={14} />
            <span className="hidden sm:inline">{meta.label}</span>
          </button>
          <button
            data-testid="nav-invite-cta-button"
            onClick={() => scrollToId("contact")}
            className="hidden rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-transform hover:scale-[1.04] sm:block"
            style={{ backgroundColor: "var(--primary-btn-bg)", color: "var(--primary-btn-text)" }}
          >
            {t("nav.invite")}
          </button>
        </div>
      </div>
    </header>
  );
}
